import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Mail, Lock, User, Eye, EyeOff } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_11207ce1-f0ad-4af8-83b6-64fb331f35a6/artifacts/e6s5tkbt_OSSuccess6.png";

export default function AuthPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get('redirect');
  const action = searchParams.get('action');
  
  const { isDarkMode } = useTheme();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "student",
    state: "",
    license_number: "",
    agd_number: ""
  });

  // If action=start, show signup form
  useEffect(() => {
    if (action === 'start') {
      setIsLogin(false);
    }
  }, [action]);

  // Theme-aware colors for form elements
  const formColors = {
    cardBg: isDarkMode ? '#0f2744' : 'white',
    heading: isDarkMode ? '#ffffff' : '#092036',
    text: isDarkMode ? '#e2e8f0' : '#092036',
    subtext: isDarkMode ? '#94a3b8' : '#70767C',
    inputBg: isDarkMode ? '#ffffff' : 'white',
    inputText: isDarkMode ? '#092036' : '#092036',
    inputBorder: isDarkMode ? '#e2e8f0' : '#DADEEO',
    link: isDarkMode ? '#60a5fa' : '#102862',
    divider: isDarkMode ? '#1e3a5f' : '#DADEEO',
  };

  const handleForgotPassword = async () => {
    if (!resetEmail) {
      toast.error("Please enter your email address");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: resetEmail });
      toast.success("Password reset instructions sent to your email!");
      setShowForgotPassword(false);
      setResetEmail("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reset email");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!isLogin && !formData.name) {
      toast.error("Please enter your name");
      return;
    }

    // Check passwords match on signup
    if (!isLogin && formData.password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    // Minimum password length
    if (!isLogin && formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/register";
      
      // Create FormData
      const formPayload = new FormData();
      formPayload.append("email", formData.email);
      formPayload.append("password", formData.password);
      if (!isLogin) {
        formPayload.append("name", formData.name);
        formPayload.append("role", formData.role);
      }

      const response = await axios.post(`${API}${endpoint}`, formPayload, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      localStorage.setItem("session_token", response.data.session_token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      
      toast.success(isLogin ? "Logged in successfully!" : "Account created successfully!");
      
      // Redirect to original URL if provided, otherwise based on role
      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else if (response.data.user.role === "admin") {
        window.location.href = "/admin";
      } else {
        window.location.href = "/dashboard";
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || "Authentication failed";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: isDarkMode ? '#092036' : '#ffffff' }}>
      {/* Header */}
      <header className={isDarkMode ? '' : 'backdrop-blur-md border-b'} style={{ 
        backgroundColor: isDarkMode ? '#092036' : 'rgba(255, 255, 255, 0.9)',
        borderColor: isDarkMode ? 'transparent' : '#DADEEO' 
      }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center">
          <button 
            onClick={() => navigate("/")}
            className="flex items-center gap-2 sm:gap-3 hover:opacity-80 transition-opacity min-w-0"
          >
            <img src={LOGO_URL} alt="Oral Surgery Success" className="h-10 w-10 sm:h-12 sm:w-12 object-contain flex-shrink-0" style={isDarkMode ? { filter: 'drop-shadow(0 0 2px rgba(255,255,255,0.8))' } : {}} />
            <span className="text-base sm:text-2xl font-bold truncate" style={{ color: isDarkMode ? '#ffffff' : '#092036' }}>Oral Surgery Success</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 pb-6 sm:pb-12 sm:pt-6">
        <Card className="w-full max-w-4xl p-8 shadow-2xl" style={{ backgroundColor: formColors.cardBg }}>
          <div className="text-center mb-8">
            <img src={LOGO_URL} alt="Oral Surgery Success" className="h-20 w-20 object-contain mx-auto mb-4" style={isDarkMode ? { filter: 'drop-shadow(0 0 2px rgba(255,255,255,0.8))' } : {}} />
            <h1 className="text-3xl font-bold mb-2" style={{ color: formColors.heading }}>
              {isLogin ? "Welcome Back" : "Get Started Here"}
            </h1>
            <p className="text-sm" style={{ color: formColors.subtext }}>
              {isLogin ? "Sign in to continue your learning" : "Create your account in 30 seconds"}
            </p>
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: formColors.text }}>
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: formColors.subtext }} />
                    <Input
                      type="text"
                      placeholder="Dr. John Smith"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="pl-10 py-6"
                      style={{ backgroundColor: formColors.inputBg, color: formColors.inputText, borderColor: formColors.inputBorder }}
                      required={!isLogin}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: formColors.text }}>
                      State <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.state}
                      onChange={(e) => setFormData({...formData, state: e.target.value})}
                      className="w-full px-3 py-3 border rounded-md"
                      style={{ backgroundColor: formColors.inputBg, color: formColors.inputText, borderColor: formColors.inputBorder }}
                      required={!isLogin}
                    >
                      <option value="">Select State</option>
                      <option value="AL">Alabama</option>
                      <option value="AK">Alaska</option>
                      <option value="AZ">Arizona</option>
                      <option value="AR">Arkansas</option>
                      <option value="CA">California</option>
                      <option value="CO">Colorado</option>
                      <option value="CT">Connecticut</option>
                      <option value="DE">Delaware</option>
                      <option value="FL">Florida</option>
                      <option value="GA">Georgia</option>
                      <option value="HI">Hawaii</option>
                      <option value="ID">Idaho</option>
                      <option value="IL">Illinois</option>
                      <option value="IN">Indiana</option>
                      <option value="IA">Iowa</option>
                      <option value="KS">Kansas</option>
                      <option value="KY">Kentucky</option>
                      <option value="LA">Louisiana</option>
                      <option value="ME">Maine</option>
                      <option value="MD">Maryland</option>
                      <option value="MA">Massachusetts</option>
                      <option value="MI">Michigan</option>
                      <option value="MN">Minnesota</option>
                      <option value="MS">Mississippi</option>
                      <option value="MO">Missouri</option>
                      <option value="MT">Montana</option>
                      <option value="NE">Nebraska</option>
                      <option value="NV">Nevada</option>
                      <option value="NH">New Hampshire</option>
                      <option value="NJ">New Jersey</option>
                      <option value="NM">New Mexico</option>
                      <option value="NY">New York</option>
                      <option value="NC">North Carolina</option>
                      <option value="ND">North Dakota</option>
                      <option value="OH">Ohio</option>
                      <option value="OK">Oklahoma</option>
                      <option value="OR">Oregon</option>
                      <option value="PA">Pennsylvania</option>
                      <option value="RI">Rhode Island</option>
                      <option value="SC">South Carolina</option>
                      <option value="SD">South Dakota</option>
                      <option value="TN">Tennessee</option>
                      <option value="TX">Texas</option>
                      <option value="UT">Utah</option>
                      <option value="VT">Vermont</option>
                      <option value="VA">Virginia</option>
                      <option value="WA">Washington</option>
                      <option value="WV">West Virginia</option>
                      <option value="WI">Wisconsin</option>
                      <option value="WY">Wyoming</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: formColors.text }}>
                      License Number <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="text"
                      placeholder="12345"
                      value={formData.license_number}
                      onChange={(e) => setFormData({...formData, license_number: e.target.value})}
                      className="py-3"
                      style={{ backgroundColor: formColors.inputBg, color: formColors.inputText, borderColor: formColors.inputBorder }}
                      required={!isLogin}
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: formColors.text }}>
                    AGD Number <span className="text-xs" style={{ color: formColors.subtext }}>(Optional)</span>
                  </label>
                  <Input
                    type="text"
                    placeholder="AGD123456"
                    value={formData.agd_number}
                    onChange={(e) => setFormData({...formData, agd_number: e.target.value})}
                    className="py-3"
                    style={{ backgroundColor: formColors.inputBg, color: formColors.inputText, borderColor: formColors.inputBorder }}
                  />
                  <p className="text-xs mt-1" style={{ color: formColors.subtext }}>If you're an AGD member, enter your AGD number</p>
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: formColors.text }}>
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: formColors.subtext }} />
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="pl-10 py-6"
                  style={{ backgroundColor: formColors.inputBg, color: formColors.inputText, borderColor: formColors.inputBorder }}
                  required
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium" style={{ color: formColors.text }}>
                  Password
                </label>
                {isLogin && (
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm hover:underline"
                    style={{ color: formColors.link }}
                  >
                    Forgot Password?
                  </button>
                )}
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: formColors.subtext }} />
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="pl-10 pr-10 py-6"
                  style={{ backgroundColor: formColors.inputBg, color: formColors.inputText, borderColor: formColors.inputBorder }}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 hover:opacity-70 transition-opacity"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" style={{ color: formColors.subtext }} />
                  ) : (
                    <Eye className="w-5 h-5" style={{ color: formColors.subtext }} />
                  )}
                </button>
              </div>
            </div>

            {/* Confirm Password - Only show on signup */}
            {!isLogin && (
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: formColors.text }}>
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: formColors.subtext }} />
                  <Input
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 pr-10 py-6"
                    style={{ 
                      backgroundColor: formColors.inputBg, 
                      color: formColors.inputText, 
                      borderColor: confirmPassword && confirmPassword !== formData.password ? '#ef4444' : formColors.inputBorder 
                    }}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 hover:opacity-70 transition-opacity"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="w-5 h-5" style={{ color: formColors.subtext }} />
                    ) : (
                      <Eye className="w-5 h-5" style={{ color: formColors.subtext }} />
                    )}
                  </button>
                </div>
                {confirmPassword && confirmPassword !== formData.password && (
                  <p className="text-red-500 text-sm mt-1">Passwords do not match</p>
                )}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full py-6 text-lg font-semibold text-white border-0"
              style={{ background: '#FF6B35' }}
            >
              {loading ? "Please wait..." : (isLogin ? "Sign In" : "Create Account")}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-sm hover:underline"
              style={{ color: formColors.link }}
            >
              {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
            </button>
          </div>

          {/* Forgot Password Modal */}
          {showForgotPassword && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <Card className="w-full max-w-md p-6" style={{ backgroundColor: formColors.cardBg }}>
                <h2 className="text-2xl font-bold mb-2" style={{ color: formColors.heading }}>
                  Reset Password
                </h2>
                <p className="text-sm mb-6" style={{ color: formColors.subtext }}>
                  Enter your email address and we'll send you instructions to reset your password.
                </p>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2" style={{ color: formColors.text }}>
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5" style={{ color: formColors.subtext }} />
                    <Input
                      type="email"
                      placeholder="you@example.com"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      className="pl-10 py-6"
                      style={{ backgroundColor: formColors.inputBg, color: formColors.inputText, borderColor: formColors.inputBorder }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleForgotPassword();
                        }
                      }}
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={() => {
                      setShowForgotPassword(false);
                      setResetEmail("");
                    }}
                    variant="outline"
                    className="flex-1 py-6"
                    style={{ borderColor: formColors.inputBorder, color: formColors.text }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleForgotPassword}
                    disabled={loading}
                    className="flex-1 py-6 text-white border-0"
                    style={{ background: '#FF6B35' }}
                  >
                    {loading ? "Sending..." : "Send Reset Link"}
                  </Button>
                </div>
              </Card>
            </div>
          )}

          {/* AGD PACE Badge - Centered below sign-in */}
          <div className="mt-8 pt-6 border-t flex justify-center" style={{ borderColor: formColors.divider }}>
            <div className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_dentalce-hub/artifacts/20ojtj4k_agd-pace-logo-bw.png" 
                alt="AGD PACE Approved Provider" 
                className="h-20 object-contain flex-shrink-0"
                style={isDarkMode ? { filter: 'invert(1) contrast(1.1)' } : {}}
              />
              <div className="text-xs text-left" style={{ color: formColors.text, fontFamily: 'Arial, Helvetica, sans-serif', lineHeight: '1.2' }}>
                <p className="font-bold">Oral Surgery Success</p>
                <p>Nationally Approved PACE Program Provider for FAGD/MAGD credit.</p>
                <p>Approval does not imply acceptance by any regulatory authority or AGD endorsement.</p>
                <p className="font-semibold">10/01/2025 to 09/30/2027</p>
                <p className="font-semibold">Provider ID #421884</p>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Footer */}
      <footer className="py-6 px-6 text-center text-sm" style={{ color: isDarkMode ? '#94a3b8' : '#70767C' }}>
        <p>© 2025 Oral Surgery Success. All rights reserved.</p>
      </footer>
    </div>
  );
}
